/**
 * imageCompression - the single place the app shrinks a field photo before
 * it goes anywhere near the network.
 *
 * Why this exists as a util rather than living in each capture component:
 * every surface that accepts a photo (PhotoGrid on the detail pages, the
 * per-item workflow modal, the verify step, avatars) has to obey the same
 * server ceiling, and we were previously carrying three near-identical
 * copies of the browser-image-compression call with different numbers in
 * them. One helper, one set of constants, one guarantee.
 *
 * The guarantee: whatever comes back from `compressPhoto` is at or below
 * MAX_UPLOAD_BYTES — the backend's MAX_UPLOAD_SIZE_MB (10 MB) validation
 * cap. We aim far lower than that (TARGET_MB) because the real constraint
 * in the field isn't the server's rule, it's a technician on one bar of
 * LTE in a stairwell. A ~1 MB JPEG uploads over that; a 12 MB iPhone HEIC
 * does not.
 *
 * How it gets there:
 *   1. browser-image-compression in a Web Worker at the default settings
 *      (1600px longest edge, ~1 MB, quality 0.7). Off the main thread so
 *      the UI keeps responding while a big image is re-encoded.
 *   2. If that lands over the hard cap anyway (pathological sources do
 *      exist), walk the RETRY_STEPS ladder — progressively smaller and
 *      lossier — until it fits.
 *   3. If the worker path throws entirely (older mobile WebViews ship
 *      without it), fall back to a plain canvas resize.
 *   4. If the source is HEIC and the browser simply can't decode it
 *      (Chrome/Firefox can't), hand back the ORIGINAL file when it's
 *      already under the cap. The backend accepts HEIC on purpose — see
 *      App\Rules\FieldPhoto — and converts server-side, so an
 *      undecodable HEIC should cost the tech an unoptimized upload, not
 *      a rejected photo.
 *   5. Only when none of that produces something under the cap do we
 *      throw, and the message tells the user the actual size.
 */
import imageCompression from 'browser-image-compression';

/* ============================================
 * CONSTANTS
 * ============================================ */

/** Server-side ceiling. Mirrors MAX_UPLOAD_SIZE_MB in the Laravel .env
 *  (see UploadController/WorkOrderController/InspectionController). Nothing
 *  this module returns may exceed it. */
export const MAX_UPLOAD_MB = 10;

/** MAX_UPLOAD_MB expressed in bytes, for direct File.size comparisons. */
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

/** What we actually aim for. Well under the cap — field connections, not
 *  server rules, are the binding constraint. */
const TARGET_MB = 1;

/** Longest edge after resize. Above the backend's own 1920px downscale
 *  would be wasted bytes; 1600px keeps detail legible in the PDF report
 *  while cutting a 12MP phone photo down by an order of magnitude. */
const MAX_DIMENSION = 1600;

/** JPEG quality for the first pass and the canvas fallback. */
const QUALITY = 0.7;

/** Escalating fallbacks, tried in order, if a pass still lands over the
 *  hard cap. Each step is smaller and lossier than the last. */
const RETRY_STEPS = [
    { maxSizeMB: TARGET_MB, maxWidthOrHeight: 1280, initialQuality: 0.6 },
    { maxSizeMB: 0.8, maxWidthOrHeight: 1024, initialQuality: 0.5 },
    { maxSizeMB: 0.5, maxWidthOrHeight: 800, initialQuality: 0.4 },
];

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Result of a compression pass.
 *
 * @property file      - The compressed image, ready to hand to uploadService
 * @property dataUrl   - Same bytes as a data URL, for instant local preview
 *                       and for the offline queue (which is JSON, so it
 *                       cannot hold a File)
 * @property sizeBytes - Byte length of `file`, so callers don't re-measure
 */
export interface CompressedPhoto {
    file: File;
    dataUrl: string;
    sizeBytes: number;
}

/** Per-call overrides. Omit everything for the field-photo defaults. */
export interface CompressPhotoOptions {
    /** Target size in megabytes (best effort — the hard cap still wins). */
    maxSizeMB?: number;
    /** Longest edge in pixels; aspect ratio is preserved. */
    maxWidthOrHeight?: number;
    /** Initial JPEG quality, 0–1. */
    quality?: number;
}

/* ============================================
 * HELPERS
 * ============================================ */

/**
 * Detects HEIC/HEIF by MIME type or extension. iOS shoots HEIC by default,
 * and browsers report it inconsistently (sometimes an empty type, sometimes
 * application/octet-stream), so both checks are needed.
 */
export function isHEICFile(file: File): boolean {
    const type = file.type.toLowerCase();
    const mimeCheck = type.includes('heic') || type.includes('heif');
    const extCheck = /\.(heic|heif)$/i.test(file.name);
    return mimeCheck || extCheck;
}

/**
 * Rewrites a filename to a .jpg extension. We always re-encode to JPEG, so
 * the name has to follow — the backend's extension checks reject a JPEG
 * still calling itself IMG_1234.HEIC.
 */
export function toJpegName(name: string): string {
    const base = name.replace(/\.[^./\\]+$/, '');
    return `${base || 'photo'}.jpg`;
}

/** Reads a Blob into a data URL. */
export function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read image data'));
        reader.readAsDataURL(blob);
    });
}

/**
 * Turns a data URL back into a File. Used by the offline path, where the
 * queued photo survives as JSON and has to become a File again at sync
 * time, and by anything that still holds only a base64 capture.
 */
export async function dataUrlToFile(dataUrl: string, fileName: string, mimeType?: string): Promise<File> {
    const blob = await (await fetch(dataUrl)).blob();
    return new File([blob], fileName, { type: mimeType || blob.type || 'image/jpeg' });
}

/** Formats a byte count for user-facing error copy ("12.4 MB"). */
export function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Canvas-based resize + JPEG re-encode. The fallback for WebViews where
 * the browser-image-compression worker isn't available. Cruder than the
 * library (single pass, no size targeting) but it only needs to be good
 * enough to get under the cap.
 */
function canvasResize(file: File, maxDimension: number, quality: number): Promise<File> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            /* Release the blob URL as soon as the bitmap is decoded —
               these leak for the page's lifetime otherwise. */
            URL.revokeObjectURL(objectUrl);

            let { width, height } = img;
            if (width > maxDimension || height > maxDimension) {
                const ratio = Math.min(maxDimension / width, maxDimension / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas is unavailable on this device'));
                return;
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('Failed to encode the image'));
                        return;
                    }
                    resolve(new File([blob], toJpegName(file.name), { type: 'image/jpeg' }));
                },
                'image/jpeg',
                quality,
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Failed to decode the image'));
        };

        img.src = objectUrl;
    });
}

/** One browser-image-compression pass, normalized to a .jpg-named File. */
async function compressOnce(
    file: File,
    settings: { maxSizeMB: number; maxWidthOrHeight: number; initialQuality: number },
): Promise<File> {
    const blob = await imageCompression(file, {
        maxSizeMB: settings.maxSizeMB,
        maxWidthOrHeight: settings.maxWidthOrHeight,
        initialQuality: settings.initialQuality,
        fileType: 'image/jpeg',
        useWebWorker: true,
    });
    return new File([blob], toJpegName(file.name), { type: 'image/jpeg' });
}

/* ============================================
 * PUBLIC API
 * ============================================ */

/**
 * Compresses a captured or selected image down to something a field
 * connection can actually push, guaranteeing the result fits under the
 * server's upload cap.
 *
 * @param file    - The source image, straight from the camera or picker
 * @param options - Optional overrides for the target size / dimension
 * @returns The compressed File, a preview data URL, and the byte size
 * @throws If the image cannot be brought under MAX_UPLOAD_BYTES
 */
export async function compressPhoto(file: File, options?: CompressPhotoOptions): Promise<CompressedPhoto> {
    const first = {
        maxSizeMB: options?.maxSizeMB ?? TARGET_MB,
        maxWidthOrHeight: options?.maxWidthOrHeight ?? MAX_DIMENSION,
        initialQuality: options?.quality ?? QUALITY,
    };

    let result: File | null = null;

    try {
        result = await compressOnce(file, first);

        /* Belt and braces: the library treats maxSizeMB as a target, not a
           promise. Walk the ladder if the first pass overshot the cap. */
        for (const step of RETRY_STEPS) {
            if (result.size <= MAX_UPLOAD_BYTES) break;
            result = await compressOnce(file, step);
        }
    } catch (err) {
        /* Worker path unavailable or the source is a format this browser
           can't decode. Try the canvas route before giving up. */
        console.warn('[imageCompression] Worker compression failed, falling back to canvas:', err);
        try {
            result = await canvasResize(file, first.maxWidthOrHeight, first.initialQuality);
            for (const step of RETRY_STEPS) {
                if (result.size <= MAX_UPLOAD_BYTES) break;
                result = await canvasResize(file, step.maxWidthOrHeight, step.initialQuality);
            }
        } catch (fallbackErr) {
            console.warn('[imageCompression] Canvas fallback failed:', fallbackErr);
            result = null;
        }
    }

    /* Nothing could decode it. If it's a HEIC the browser can't read, the
       server still can — send the original rather than losing the photo. */
    if (!result) {
        if (isHEICFile(file) && file.size <= MAX_UPLOAD_BYTES) {
            console.info('[imageCompression] HEIC could not be decoded locally — uploading the original for server-side conversion');
            return { file, dataUrl: await blobToDataUrl(file), sizeBytes: file.size };
        }
        throw new Error('This image could not be processed. Try taking the photo again.');
    }

    /* Last line of defense — never hand the caller something the server
       will reject with a 422. */
    if (result.size > MAX_UPLOAD_BYTES) {
        throw new Error(
            `This image is still ${formatBytes(result.size)} after compression, over the ${MAX_UPLOAD_MB} MB limit. Try a lower-resolution photo.`,
        );
    }

    return { file: result, dataUrl: await blobToDataUrl(result), sizeBytes: result.size };
}

export default compressPhoto;
